import { Box, Flex, HStack, Menu, Portal, Text, chakra } from '@chakra-ui/react';
import { Check, Copy, Info, RefreshCcw } from 'lucide-react';
import { useState, type ReactNode } from 'react';

interface AppMenuProps {
  apiBaseUrl: string;
  apiStatus: string;
  buildLabel: string;
  isLive: boolean;
  onOpenAbout: () => void;
  onRefreshData: () => void;
}

export function AppMenu({
  apiBaseUrl,
  apiStatus,
  buildLabel,
  isLive,
  onOpenAbout,
  onRefreshData,
}: AppMenuProps) {
  const [copied, setCopied] = useState(false);

  async function copyAPIBaseURL() {
    await navigator.clipboard.writeText(apiBaseUrl);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1_200);
  }

  return (
    <Menu.Root positioning={{ placement: 'bottom-end' }} lazyMount unmountOnExit>
      <Menu.Trigger asChild>
        <chakra.button
          type="button"
          aria-label="Open Oddsquake app menu"
          display="grid"
          h="11"
          w="11"
          placeItems="center"
          borderRadius="full"
          borderWidth="1px"
          borderColor="border.subtle"
          bgGradient="to-br"
          gradientFrom="oddsquake.panel2"
          gradientTo="oddsquake.void"
          fontSize="sm"
          fontWeight="bold"
          transition="all 0.2s"
          _hover={{ borderColor: 'signal.blue/40', color: 'white' }}
        >
          OQ
        </chakra.button>
      </Menu.Trigger>

      <Portal>
        <Menu.Positioner>
          <Menu.Content
            zIndex="popover"
            w="72"
            overflow="hidden"
            borderRadius="oqPanel"
            borderWidth="1px"
            borderColor="border.subtle"
            bg="surface.panel"
            boxShadow="2xl"
          >
            <Box borderBottomWidth="1px" borderBottomColor="border.subtle" px="4" py="3">
              <Text fontSize="sm" fontWeight="semibold" color="text.primary">
                Oddsquake
              </Text>
              <Text mt="1" fontSize="xs" color="text.muted">
                Build {buildLabel}
              </Text>
            </Box>
            <Box borderBottomWidth="1px" borderBottomColor="border.subtle" px="4" py="3" fontSize="sm">
              <Flex align="center" justify="space-between" gap="3">
                <Text color="text.muted">API status</Text>
                <HStack gap="2" fontWeight="semibold" color="text.primary">
                  <Box
                    h="2"
                    w="2"
                    borderRadius="full"
                    bg={isLive ? 'signal.good' : 'signal.warn'}
                    boxShadow={isLive ? '0 0 18px var(--oq-colors-signal-good)' : undefined}
                  />
                  <Text>{apiStatus}</Text>
                </HStack>
              </Flex>
              <Text mt="2" truncate fontSize="xs" color="text.muted">
                {apiBaseUrl}
              </Text>
            </Box>
            <Box p="1">
              <MenuButton value="refresh" icon={<RefreshCcw />} label="Refresh data" onSelect={onRefreshData} />
              <MenuButton
                value="copy-api-base-url"
                icon={copied ? <Check /> : <Copy />}
                label={copied ? 'Copied API base URL' : 'Copy API base URL'}
                onSelect={() => {
                  void copyAPIBaseURL();
                }}
              />
              <MenuButton value="about" icon={<Info />} label="About" onSelect={onOpenAbout} />
            </Box>
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
}

function MenuButton({
  value,
  icon,
  label,
  onSelect,
}: {
  value: string;
  icon: ReactNode;
  label: string;
  onSelect: () => void | Promise<void>;
}) {
  return (
    <Menu.Item
      value={value}
      onSelect={() => {
        void onSelect();
      }}
      display="flex"
      w="full"
      alignItems="center"
      gap="3"
      borderRadius="md"
      px="3"
      py="2.5"
      textAlign="left"
      fontSize="sm"
      fontWeight="medium"
      color="text.muted"
      transition="all 0.2s"
      _hover={{ bg: 'whiteAlpha.50', color: 'text.primary' }}
      _highlighted={{ bg: 'whiteAlpha.50', color: 'text.primary' }}
    >
      <Box mr="1" color="text.muted" css={{ '& > svg': { height: '1rem', width: '1rem' } }}>
        {icon}
      </Box>
      {label}
    </Menu.Item>
  );
}